import React, { useMemo } from 'react';
import type { CourseDiscountType } from '../../types';
import PersianDatePicker from '../PersianDatePicker';
import { coreFieldClasses } from './CourseCoreFormLayout';

export type CoursePricingFormValues = {
  listPrice: number;
  discountType: CourseDiscountType;
  discountPercent: number;
  discountAmount: number;
  discountStartsAt: string | null;
  discountEndsAt: string | null;
};

/** همان خروجی PersianDatePicker یا رشتهٔ محلی `YYYY-MM-DDTHH:mm:ss` → ISO برای API */
function localInputToIso(localStr: string): string | null {
  if (!localStr.trim()) return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function previewPayable(v: CoursePricingFormValues): number {
  const base = Math.max(0, Number(v.listPrice) || 0);
  if (v.discountType === 'NONE') return Math.round(base);
  if (v.discountType === 'PERCENT') {
    const pct = Math.min(100, Math.max(0, Number(v.discountPercent) || 0));
    return Math.max(0, Math.round(base - (base * pct) / 100));
  }
  const off = Math.max(0, Number(v.discountAmount) || 0);
  return Math.max(0, Math.round(base - off));
}

type Props = {
  values: CoursePricingFormValues;
  onChange: (patch: Partial<CoursePricingFormValues>) => void;
  /** وقتی دوره «رایگان» است؛ فیلدها غیرفعال و فقط پیش‌نمایش صفر نشان داده می‌شود */
  locked?: boolean;
};

export default function CoursePricingAdminBlock({ values, onChange, locked = false }: Props) {
  const payablePreview = useMemo(() => previewPayable(values), [values]);

  return (
    <div
      className={`space-y-4 rounded-xl border border-violet-100 bg-violet-50/40 p-4 ${locked ? 'opacity-80' : ''}`}
    >
      {locked ? (
        <p className="rounded-lg border border-teal-200 bg-white/90 px-3 py-2 text-[11px] font-semibold leading-relaxed text-teal-900">
          قیمت‌گذاری برای دورهٔ رایگان خاموش است؛ مبلغ ثبت‌شده روی سرور صفر و بدون تخفیف ذخیره می‌شود.
        </p>
      ) : null}
      <fieldset disabled={locked} className="space-y-4 min-w-0 border-0 p-0 m-0">
      <div>
        <label className={coreFieldClasses.label}>قیمت پایه (لیست، تومان)</label>
        <p className="mb-2 text-[11px] text-slate-600">
          مبنای فروش قبل از هر تخفیف. روی سایت، اگر تخفیف فعال باشد خط‌خورده نمایش داده می‌شود و کاربر طبق «قابل پرداخت» و تایمر زمانی عمل می‌کند.
        </p>
        <input
          type="number"
          value={values.listPrice || 0}
          onChange={(e) => onChange({ listPrice: Math.max(0, Number(e.target.value) || 0) })}
          className={coreFieldClasses.input}
          min={0}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={coreFieldClasses.label}>نوع تخفیف</label>
          <select
            value={values.discountType}
            onChange={(e) =>
              onChange({
                discountType: e.target.value as CourseDiscountType,
              })
            }
            className={coreFieldClasses.select}
          >
            <option value="NONE">بدون تخفیف</option>
            <option value="PERCENT">درصدی از قیمت پایه</option>
            <option value="FIXED">مبلغ ثابت (کسر از قیمت پایه)</option>
          </select>
        </div>

        {values.discountType === 'PERCENT' && (
          <div>
            <label className={coreFieldClasses.label}>درصد تخفیف (۰–۱۰۰)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={values.discountPercent}
              onChange={(e) => onChange({ discountPercent: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
              className={coreFieldClasses.input}
            />
          </div>
        )}

        {values.discountType === 'FIXED' && (
          <div>
            <label className={coreFieldClasses.label}>مبلغ تخفیف (تومان)</label>
            <input
              type="number"
              min={0}
              value={values.discountAmount}
              onChange={(e) => onChange({ discountAmount: Math.max(0, Number(e.target.value) || 0) })}
              className={coreFieldClasses.input}
            />
          </div>
        )}
      </div>

      {(values.discountType === 'PERCENT' || values.discountType === 'FIXED') && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className={coreFieldClasses.label}>شروع اعمال تخفیف</label>
            <p className="mb-1 text-[11px] text-slate-500">خالی = بدون محدودیت شروع از همین الان؛ تقویم شمسی مانند لیدها.</p>
            <PersianDatePicker
              value={values.discountStartsAt ?? ''}
              onChange={(v) =>
                onChange({
                  discountStartsAt: v.trim() ? localInputToIso(v.trim()) : null,
                })
              }
              placeholder="تاریخ و ساعت شروع (شمسی)"
              className="w-full"
            />
          </div>
          <div>
            <label className={coreFieldClasses.label}>پایان تخفیف (تایمر روی سایت)</label>
            <p className="mb-1 text-[11px] text-slate-500">خالی = بدون پایان زمانی؛ برای نمایش تایمر روی سایت پر کنید.</p>
            <PersianDatePicker
              value={values.discountEndsAt ?? ''}
              onChange={(v) =>
                onChange({
                  discountEndsAt: v.trim() ? localInputToIso(v.trim()) : null,
                })
              }
              placeholder="تاریخ و ساعت پایان (شمسی)"
              className="w-full"
            />
          </div>
        </div>
      )}

      <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-sm text-emerald-900">
        <span className="font-bold">پیش‌نمایش قابل پرداخت الان: </span>
        {payablePreview.toLocaleString('fa-IR')} تومان
        {values.discountType !== 'NONE' && values.listPrice > 0 && payablePreview < values.listPrice && (
          <span className="mr-2 text-emerald-700">
            (حذف {Math.round(values.listPrice - payablePreview).toLocaleString('fa-IR')} از قیمت پایه)
          </span>
        )}
      </div>
      </fieldset>
    </div>
  );
}
